import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import _ from 'lodash';
import {MoreVert} from '@material-ui/icons';
import cn from 'classnames';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl';
import {useHistory} from 'react-router';
import {deleteDeploymentDelete} from '../../../api/deployment/DeploymentDelete';
import {deleteDevice} from '../../../api/device/DeviceList';
import {useTestListByLatest} from '../../../api/test/TestList';
import {StudioCheckbox} from '../../../base-components/StudioCheckbox';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import HumanReadableTime from '../../../base-components/StudioHumanReadableTime/HumanReadableTime';
import {StudioSearch} from '../../../base-components/StudioSearch';
import {StudioTab, StudioTabPanel, StudioTabs} from '../../../base-components/StudioTab';
import {toast} from '../../../base-components/StudioToast';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';
import {TestDeploymentQueryResponse} from '../../../types/deployment/TestDeploymentQueryResponse';
import {Application} from '../../../types/project/Application';
import {Project} from '../../../types/project/Project';
import {TestDeploymentInfo} from '../../../types/test/TestDeploymentInfo';
import {AddStreamingDeviceForm} from '../AddStreamingDeviceForm/AddStreamingDeviceForm';
import {DeployExportDialog} from '../DeployExportDialog';
import {DuplicateDialog} from '../DuplicateDialog';
import {DeleteApplicationDialog} from '../DeleteApplicationDialog';
import {FileUploadProgress} from '../FileExplorer';
import {
  renameDuplicates,
  resetDeploy,
  setDefaultTestName,
  setDeployConfig,
  setIsDuplicateModalOpen,
  setIsDeleteModalOpen,
  setIsExportModalOpen,
  setPendingFiles,
  setSelectedDeploymentIds,
  setStreamingDevices,
  TASK_STATUS,
  TestSourceType,
  useDeploy,
  INFERENCE_SOURCE_TEXT,
  VIDEO_SOURCES,
  getInputSourceOptions,
  setStreamReady,
} from '../useDeploy';
import {useDeployTask} from './useDeployTask';
import {useTaskManager} from './useTaskManager';
import {
  FetchAppPostProcessor,
  FetchModelPostProcessor,
  FetchPreProcessor,
  supportsImages,
  supportsVideo,
} from '../../ONNXRuntimeContainer/ONNXRuntimeContainer';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import {TEST_PANEL_SETTINGS} from '../../../config/constants';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../HlsVideoStream';
import DeployConfigPanelForm from './DeployConfigPanelForm';

import './DeployConfigPanel.scss';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store';

export const getTestSource = (test: TestDeploymentInfo | DeploymentDetailsInfo) => {
  if (test.deploymentInput?.splitType === 'TEST') {
    return <FormattedMessage id="test.testSet" />;
  }
  if (test.deploymentInput?.splitType === 'VALIDATION') {
    return <FormattedMessage id="test.validationSet" />;
  }
  if (test.deploymentInput?.splitType === 'TRAIN') {
    return <FormattedMessage id="test.trainSet" />;
  }
  return <FormattedMessage id="test.userDataFormat" />;
};

export const getTestType = (test: TestDeploymentInfo | DeploymentDetailsInfo) => {
  if (test?.deploymentType === 'VALIDATION') {
    return <FormattedMessage id="test.validationRun" />;
  }
  if (test?.deploymentType === 'SAMPLE_INFERENCE') {
    return <FormattedMessage id="test.inferenceRun" />;
  }
  if (test?.deploymentType === 'VALIDATION_PLUS_INFERENCE') {
    return <FormattedMessage id="test.testInferenceRun" />;
  }
  if (test?.deploymentType === 'INFERENCE') {
    return <FormattedMessage id="test.inferenceRun" />;
  }
  return null;
};

type HistoryItemProps = {
  deployment: TestDeploymentInfo;
  application?: Application;
  onDelete: (event: {id: string}) => void;
  onSelect: (event: {ids: string[]}) => void;
  onDeploy: (event: {id: string; applicationId?: string}) => void;
  search: string;
  selectedIds: string[];
};

const HistoryItem = ({
  deployment,
  onDelete,
  onDeploy,
  onSelect,
  application,
  selectedIds,
}: HistoryItemProps) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const accuracy =
    deployment.deploymentStatistics?.accuracy == null &&
    deployment.deploymentStatistics?.lastTop1Accuracy == null
      ? null
      : Math.max(
          deployment.deploymentStatistics?.accuracy ?? 0,
          deployment.deploymentStatistics?.lastTop1Accuracy ?? 0
        );

  return (
    <Card data-testid="test-history-item" className="dplmt-history-card">
      <CardHeader
        action={
          <>
            <IconButton
              className="dplmt-history-card-action"
              onClick={event => {
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
              }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => {
                setAnchorEl(null);
              }}
            >
              <MenuItem
                onClick={async event => {
                  event.stopPropagation();
                  onDeploy({id: deployment.id, applicationId: deployment.applicationId});
                }}
              >
                Deploy
              </MenuItem>
              <MenuItem
                onClick={async event => {
                  event.stopPropagation();
                  await deleteDeploymentDelete({deploymentId: deployment.id});
                  onDelete({id: deployment.id});
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </>
        }
        className="dplmt-history-card-header"
        title={
          <StudioCheckbox
            className="dplmt-history-card-checkbox"
            id={deployment.id}
            name={deployment.id}
            label={deployment.name}
            checked={selectedIds.includes(deployment.id)}
            nativeTooltipTitle={deployment.name}
            nativeTooltip={true}
            onChange={(_event, checked) => {
              if (checked && !selectedIds.includes(deployment.id)) {
                onSelect({
                  ids: [...selectedIds, deployment.id],
                });
              } else {
                onSelect({
                  ids: selectedIds.filter(deploymentId => deploymentId !== deployment.id),
                });
              }
            }}
          />
        }
      />
      <CardContent
        className="dplmt-history-card-contents"
        onClick={_event => {
          onSelect({ids: [deployment.id]});
        }}
      >
        <FormattedMessage
          id="testHistory.createdOn"
          values={{creationTime: <HumanReadableTime date={deployment.creationTime} />}}
        />
        <br />
        <FormattedMessage
          id="testHistory.version"
          values={{version: deployment.version}}
        />
        <br />
        {deployment.gspNodeName && (
          <>
            <FormattedMessage
              id="testHistory.device"
              values={{device: deployment.gspNodeName}}
            />
            <br />
          </>
        )}
        <FormattedMessage
          id="testHistory.applicationName"
          values={{application: deployment.applicationName}}
        />
        <br />
        {deployment.modelName && (
          <>
            <FormattedMessage
              id="testHistory.modelName"
              values={{model: deployment.modelName}}
            />
            <br />
          </>
        )}
        <FormattedMessage
          id="testHistory.datasetName"
          values={{dataset: deployment.datasetName}}
        />
        <br />
        {getTestSource(deployment) && (
          <>
            <FormattedMessage
              id="testHistory.testSource"
              values={{source: getTestSource(deployment)}}
            />
            <br />
          </>
        )}
        {getTestType(deployment) && (
          <>
            <FormattedMessage
              id="testHistory.testType"
              values={{type: getTestType(deployment)}}
            />
            <br />
          </>
        )}
        {!application?.analyticsApplication && (
          <div className="dplmt-history-card-stats">
            {(deployment.deploymentType === 'VALIDATION' ||
              deployment.deploymentType === 'SAMPLE_INFERENCE' ||
              deployment.deploymentType === 'VALIDATION_PLUS_INFERENCE') &&
              accuracy != null && (
                <div>
                  <FormattedMessage
                    id={
                      application?.model?.modelPurpose === 'Detection'
                        ? 'testHistory.map'
                        : 'testHistory.accuracy'
                    }
                    values={{
                      accuracy: (
                        <FormattedNumber
                          style="percent" // eslint-disable-line react/style-prop-object
                          maximumFractionDigits={2}
                          value={accuracy / 100}
                        />
                      ),
                    }}
                  />
                </div>
              )}
            <div>
              {deployment.deploymentStatistics?.fps !== undefined &&
                deployment.deploymentStatistics.fps > 0 && (
                  <FormattedMessage
                    id="testHistory.fps"
                    values={{
                      fps: intl.formatNumber(deployment.deploymentStatistics.fps, {
                        maximumFractionDigits: 0,
                      }),
                    }}
                  />
                )}
            </div>
            <div>
              {deployment.deploymentStatistics?.latency !== undefined &&
                deployment.deploymentStatistics.latency > 0 && (
                  <FormattedMessage
                    id="testHistory.latency"
                    values={{
                      latency: intl.formatNumber(
                        deployment.deploymentStatistics.latency,
                        {
                          style: 'unit',
                          unit: 'millisecond',
                        }
                      ),
                    }}
                  />
                )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function useResumeDeployIfExists(
  projectId: string,
  getDeploymentId: () => string | undefined,
  setDeploymentId: (id?: string) => void
) {
  const {resumeDeploy} = useDeployTask(projectId);
  useEffect(
    function resumeDeployIfExists() {
      resumeDeploy();
    },
    [resumeDeploy]
  );
}

function useDefaultConfig(
  latestApplicationId: string,
  hasDefaults: boolean,
  historyItems: TestDeploymentQueryResponse | undefined,
  projectId: string,
  onSet: () => void
) {
  const [{deployConfig}, dispatch] = useDeploy();
  const {handleSubmit} = useTaskManager(projectId);

  useEffect(() => {
    if (!hasDefaults && latestApplicationId && deployConfig.name) {
      const newDeployConfig: DeployConfigState = {
        ...deployConfig,
        deploymentId: latestApplicationId,
        testSource: '',
        testHistoryIds: historyItems?.entries?.map(item => item.id) || [],
      };
      dispatch(setDeployConfig(newDeployConfig));
      onSet();
    }
  }, [
    deployConfig,
    dispatch,
    latestApplicationId,
    handleSubmit,
    hasDefaults,
    historyItems,
    onSet,
  ]);
}

export type DeployConfigPanelProps = {
  className?: string;
  applications: Application[];
  isDeploying: boolean;
  projectData: Project;
  projectId: string;
  selectedTab: number;
  onTabChange: (tab: number) => void;
  fetchProjectData: Function;
  fetchImageSets: Function;
  fetchGspList: Function;
  shouldBeVisible: boolean;
  streamSubject: VideoStreamSubject;
};

type DeployState = ReturnType<typeof useDeploy>[0];
type DeployConfigState = DeployState['deployConfig'];

export const DeployConfigPanel = ({
  className,
  applications,
  isDeploying,
  projectData,
  projectId,
  selectedTab,
  onTabChange,
  fetchProjectData,
  fetchImageSets,
  fetchGspList,
  shouldBeVisible,
  streamSubject,
}: DeployConfigPanelProps) => {
  const history = useHistory();
  const intl = useIntl();
  const [
    {
      deployTask,
      inferenceTask,
      deployConfig,
      streamingDevices,
      isExportModalOpen,
      isDuplicateModalOpen,
      isDeleteModalOpen,
      selectedDeploymentIds,
      imageSets,
      gspList,
    },
    dispatch,
  ] = useDeploy();
  const serverConfig = useSelector((state: RootState) => state.config);
  const [deploymentId, setDeploymentId] = useState<string>();
  const {handleSubmit, handleCancel} = useTaskManager(projectId);
  const [search, setSearch] = useState('');
  const [defaultInput, setDefaultInput] = useState('');
  const [isPreProcessingValid, setIsPreProcessingValid] = useState(true);
  const [isPostProcessingValid, setIsPostProcessingValid] = useState(true);
  const [initialDevice, setInitialDevice] = useState('');
  const [confirmDeleteDevice, setConfirmDeleteDevice] = useState<{
    isOpen: boolean;
    id: string | null;
  }>({isOpen: false, id: null});
  const [confirmEndTask, setConfirmEndTask] = useState(false);
  const [hasDefaults, setHasDefaults] = useState(false);
  const [addedAsset, setAddedAsset] = useState<{name: string; url: string} | null>(null);
  const [availableTestSources, setAvailableTestSources] = useState<
    {
      name: string;
      options: {
        text: string;
        value: string;
      }[];
    }[]
  >([]);
  const [isTaskInProgress, setTaskInProgress] = useState(isDeploying);

  const latestApplicationId = useMemo(
    () =>
      applications.sort(
        (a, b) => Date.parse(b.lastUpdated) - Date.parse(a.lastUpdated)
      )?.[0]?.id ?? '',
    [applications]
  );

  const {data: historyItems, mutate: mutateHistoryItems} = useTestListByLatest({
    projectId,
    search,
  });

  useResumeDeployIfExists(projectId, () => deploymentId, setDeploymentId);

  const selectLatestHistoryItem = useCallback(
    async function selectLatestHistoryItem() {
      const items = await mutateHistoryItems();
      const latestDeploymentId = items?.entries?.[0]?.id;

      if (latestDeploymentId) {
        dispatch(setSelectedDeploymentIds([latestDeploymentId]));
      }
    },
    [dispatch, mutateHistoryItems]
  );

  useDefaultConfig(latestApplicationId, hasDefaults, historyItems, projectId, () =>
    setHasDefaults(true)
  );

  class DeployConfigObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.REMOTE_STREAM:
          setTaskInProgress(true);
          break;
        case EVENT_TYPE.ERROR:
          setTaskInProgress(isDeploying);
          break;
        case EVENT_TYPE.STREAM_FINISHED:
          setTaskInProgress(isDeploying);
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    setTaskInProgress(isDeploying);
    streamSubject.addListener('DeployConfigObserver', new DeployConfigObserver());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeploying]);

  useEffect(() => {
    if (deployTask.status === TASK_STATUS.COMPLETE) {
      selectLatestHistoryItem();
    }
  }, [deployTask.status, selectLatestHistoryItem]);

  useEffect(() => {
    const historyCount = ((historyItems?.count ?? 0) + 1).toString();
    const testName = projectData?.name
      ? projectData?.name
          .replaceAll(' ', '_')
          .slice(0, TEST_PANEL_SETTINGS.name.maxLength - historyCount.length - 1) +
        '-' +
        historyCount
      : '';
    dispatch(setDefaultTestName(testName));
  }, [dispatch, historyItems?.count, projectData?.name, projectData?.nextVersion]);

  useEffect(() => {
    dispatch(
      setDeployConfig({
        deploymentId: latestApplicationId,
        testSource: '',
        outputTypes: [],
      })
    );
  }, [latestApplicationId, dispatch]);

  const selectedApplication = applications.find(
    application => application.id === deployConfig.deploymentId
  );

  const hasIsp = Boolean(
    selectedApplication?.transformation?.transformationType === 'ISP'
  );
  const isSelectedAppONNXWeb = selectedApplication?.runtimeType === 'ONNX_RUNTIME_WEB';
  const isSelectedAppCvediaRt = selectedApplication?.runtimeType === 'CVEDIA_RT';
  const inferenceSourceTypes = selectedApplication?.inputSources?.filter(
    is =>
      is !== 'DEVICE_CAMERA' && (serverConfig.webCameraSupported || is !== 'WEB_CAMERA')
  );
  const err = (x?: string) => {
    if (x) {
      toast.error(x);
    }
  };
  const pre = FetchPreProcessor(selectedApplication, err);
  const modelPost = FetchModelPostProcessor(selectedApplication, err);
  const appPost = FetchAppPostProcessor(selectedApplication, err);
  const onnxWebImagesSupported = supportsImages(pre, modelPost, appPost);
  const onnxWebVideoSupported = supportsVideo(pre, modelPost, appPost);
  const imagesSupported =
    !isSelectedAppCvediaRt && (!isSelectedAppONNXWeb || onnxWebImagesSupported);
  const videoSupported = !hasIsp && (!isSelectedAppONNXWeb || onnxWebVideoSupported);

  useEffect(() => {
    dispatch(
      setDeployConfig(
        isSelectedAppONNXWeb
          ? {
              isONNXRuntimeWeb: true,
              testSource: 'USER_DATA',
            }
          : {isONNXRuntimeWeb: false}
      )
    );
  }, [dispatch, isSelectedAppONNXWeb]);

  useEffect(() => {
    if (!initialDevice && gspList.length > 0) {
      const dev = gspList[0].id;
      dispatch(setDeployConfig({nodeId: dev}));
      setInitialDevice(dev);
    }
  }, [dispatch, gspList, initialDevice, setInitialDevice]);

  const userDataSourceTypes = useMemo(
    () =>
      getInputSourceOptions(
        inferenceSourceTypes,
        (is: InputSource) =>
          (VIDEO_SOURCES.has(is) && videoSupported) ||
          (is === 'IMAGES' && imagesSupported)
      ),
    [inferenceSourceTypes, videoSupported, imagesSupported]
  );

  useEffect(() => {
    if (selectedApplication) {
      fetchGspList(selectedApplication.projectId, selectedApplication.id);
      if (userDataSourceTypes)
        dispatch(
          setDeployConfig({
            inferenceSource: Object.keys(userDataSourceTypes)?.[0] as InputSource,
          })
        );
    }
  }, [deployConfig.deploymentId, selectedTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const canRunWithUserData =
    userDataSourceTypes && Object.keys(userDataSourceTypes).length > 0;
  const canRunWithAppData =
    inferenceSourceTypes && inferenceSourceTypes.includes('APP_INPUT');
  const canRunWithTextData =
    inferenceSourceTypes && inferenceSourceTypes.includes('TEXT_INPUT');
  const canRunWithImageData =
    inferenceSourceTypes && inferenceSourceTypes.includes('IMAGES');

  const defaultUserDataSource = isSelectedAppONNXWeb
    ? onnxWebImagesSupported
      ? 'IMAGES'
      : onnxWebVideoSupported
      ? 'WEB_CAMERA'
      : '' // Should not be visible, see getTestSourceOptions()
    : deployConfig.inferenceSource;
  useEffect(() => {
    dispatch(setDeployConfig({inferenceSource: defaultUserDataSource}));
  }, [dispatch, defaultUserDataSource]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(debounce(setSearch, 500), []);

  const trainImagesExist = Boolean(
    selectedApplication?.dataSet?.labelDistribution?.TRAIN?.imagesCount
  );
  const testImagesExist = Boolean(
    selectedApplication?.dataSet?.labelDistribution?.TEST?.imagesCount
  );
  const validationImagesExist = Boolean(
    selectedApplication?.dataSet?.labelDistribution?.VALIDATION?.imagesCount
  );
  const datasetExists = trainImagesExist || testImagesExist || validationImagesExist;
  const datasetSourceSelected =
    deployConfig.testSource === 'TRAIN' ||
    deployConfig.testSource === 'VALIDATION' ||
    deployConfig.testSource === 'TEST';
  const readyToRunOnDataset =
    datasetSourceSelected && deployConfig.outputTypes.length > 0;
  const readyToRunOnImageSet =
    deployConfig.testSource === 'IMAGE_SETS' &&
    deployConfig.inferenceImageSetId !== undefined;
  const readyToRunOnUserData =
    (isSelectedAppONNXWeb || deployConfig.testSource === 'USER_DATA') &&
    ((deployConfig.inferenceSource === 'IMAGES' && deployConfig.files.length > 0) ||
      (deployConfig.inferenceSource === 'IP_CAMERA' && Boolean(deployConfig.ipCamera)) ||
      deployConfig.inferenceSource === 'WEB_CAMERA' ||
      (deployConfig.inferenceSource === 'FILE' && Boolean(deployConfig.videoFile)) ||
      deployConfig.inferenceSource === 'APP_INPUT' ||
      deployConfig.inferenceSource === 'TEXT_INPUT');
  const readyToRunOnAppInput = deployConfig.testSource === 'APP_INPUT';
  const readyToRunOnTextInput = deployConfig.testSource === 'TEXT_INPUT';
  const readyToRunInBrowser = isSelectedAppONNXWeb && readyToRunOnUserData;
  const readyToRunOnGsp =
    !isSelectedAppONNXWeb &&
    (deployConfig.nodeId !== undefined || isSelectedAppCvediaRt) &&
    (readyToRunOnDataset ||
      readyToRunOnImageSet ||
      readyToRunOnUserData ||
      readyToRunOnAppInput ||
      readyToRunOnTextInput);
  const readyToRun =
    selectedApplication !== undefined &&
    isPreProcessingValid &&
    isPostProcessingValid &&
    (readyToRunInBrowser || readyToRunOnGsp);

  useEffect(() => {
    const testSources = [
      ...(datasetExists &&
      !isSelectedAppONNXWeb &&
      !isSelectedAppCvediaRt &&
      canRunWithImageData
        ? [
            {
              name: intl.formatMessage({
                id: 'test.existingSource',
              }),
              options: [
                ...(trainImagesExist
                  ? [
                      {
                        value: 'TRAIN',
                        text: intl.formatMessage({
                          id: 'test.trainSet',
                        }),
                      },
                    ]
                  : []),
                ...(testImagesExist
                  ? [
                      {
                        value: 'TEST',
                        text: intl.formatMessage({
                          id: 'test.testSet',
                        }),
                      },
                    ]
                  : []),
                ...(validationImagesExist
                  ? [
                      {
                        value: 'VALIDATION',
                        text: intl.formatMessage({
                          id: 'test.validationSet',
                        }),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      {
        name: intl.formatMessage({
          id: 'test.customSource',
        }),
        options: [
          ...(canRunWithUserData
            ? [
                {
                  value: 'USER_DATA',
                  text: intl.formatMessage({
                    id: 'test.userData',
                  }),
                },
              ]
            : []),
          ...(canRunWithAppData
            ? [
                {
                  value: 'APP_INPUT',
                  text: INFERENCE_SOURCE_TEXT.get('APP_INPUT') || '',
                },
              ]
            : []),
          ...(canRunWithTextData
            ? [
                {
                  value: 'TEXT_INPUT',
                  text: INFERENCE_SOURCE_TEXT.get('TEXT_INPUT') || '',
                },
              ]
            : []),
          ...(canRunWithImageData && imageSets.length > 0
            ? [
                {
                  value: 'IMAGE_SETS',
                  text: intl.formatMessage({
                    id: 'test.imageSets',
                  }),
                },
              ]
            : []),
        ],
      },
    ];
    setAvailableTestSources(testSources);
  }, [
    intl,
    datasetExists,
    isSelectedAppONNXWeb,
    isSelectedAppCvediaRt,
    trainImagesExist,
    testImagesExist,
    validationImagesExist,
    canRunWithUserData,
    canRunWithAppData,
    canRunWithTextData,
    canRunWithImageData,
    imageSets,
  ]);

  useEffect(() => {
    const isTestInOptions = availableTestSources?.[0]?.options?.findIndex(
      option => option?.value === 'TEST'
    );
    const firstOption = availableTestSources?.[0]?.options?.[0]?.value;
    const preselectedOption = isSelectedAppCvediaRt
      ? 'APP_INPUT'
      : isTestInOptions === 1
      ? 'TEST'
      : ((firstOption || '') as TestSourceType);

    let newDeployConfig: Partial<DeployConfigState> = {
      testSource:
        deployConfig.testSource.length > 0 ? deployConfig.testSource : preselectedOption,
      inferenceSource:
        deployConfig.testSource === 'APP_INPUT'
          ? 'APP_INPUT'
          : Object.keys(userDataSourceTypes || {}).includes(deployConfig.inferenceSource)
          ? deployConfig.inferenceSource
          : (Object.keys(userDataSourceTypes || {})?.sort()?.[0] as InputSource),
    };

    if (isSelectedAppONNXWeb) {
      newDeployConfig = {
        ...newDeployConfig,
        isONNXRuntimeWeb: true,
        testSource: 'USER_DATA',
        inferenceSource: Object.keys(userDataSourceTypes || {}).includes(
          deployConfig.inferenceSource
        )
          ? deployConfig.inferenceSource
          : Object.keys(userDataSourceTypes || {}).includes('IMAGES')
          ? 'IMAGES'
          : (Object.keys(userDataSourceTypes || {})?.sort()?.[0] as InputSource),
      };
    } else if (isSelectedAppCvediaRt) {
      newDeployConfig = {
        ...newDeployConfig,
        isONNXRuntimeWeb: false,
        testSource: deployConfig.testSource || 'APP_INPUT',
        inferenceSource: Object.keys(userDataSourceTypes || {}).includes(
          deployConfig.inferenceSource
        )
          ? deployConfig.inferenceSource
          : 'APP_INPUT',
      };
    } else {
      newDeployConfig = {
        ...newDeployConfig,
        isONNXRuntimeWeb: false,
      };
    }

    dispatch(setDeployConfig(newDeployConfig));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, availableTestSources, isSelectedAppCvediaRt, isSelectedAppONNXWeb]);

  const videoFiles = useMemo(
    () => streamingDevices.filter(device => device.type === 'FILE'),
    [streamingDevices]
  );
  const ipCameras = useMemo(
    () => streamingDevices.filter(device => device.type === 'IP_CAMERA'),
    [streamingDevices]
  );

  useEffect(() => {
    if (addedAsset) {
      const {name, url} = addedAsset;
      let newDeployConfig;

      switch (deployConfig.inferenceSource) {
        case 'FILE':
          const videoFile = videoFiles.find(
            device => device.name === name && device.url === url
          )?.id;

          if (videoFile) {
            newDeployConfig = {videoFile};
          }
          break;
        case 'IP_CAMERA':
          const ipCamera = ipCameras.find(
            device => device.name === name && device.url === url
          )?.id;

          if (ipCamera) {
            newDeployConfig = {ipCamera};
          }
          break;
        default:
          break;
      }

      if (newDeployConfig) {
        dispatch(setDeployConfig(newDeployConfig));
        setAddedAsset(null);
      }
    }
  }, [ipCameras, videoFiles, addedAsset, deployConfig, dispatch]);

  const handleDeviceDelete = async (id: string) => {
    try {
      const response = await deleteDevice(id);
      if (!response?.errors?.length) {
        dispatch(setDeployConfig({ipCamera: ''}));
        dispatch(setDeployConfig({videoFile: ''}));
        dispatch(
          setStreamingDevices(streamingDevices.filter(device => device.id !== id))
        );
        toast.success(
          intl.formatMessage(
            {id: 'test.deleteDeviceSuccessMessage'},
            {
              type: _.startCase(
                deployConfig.inferenceSource === 'FILE'
                  ? intl.formatMessage({id: 'test.form.videoFile'})
                  : intl.formatMessage({id: 'test.form.ipCamera'})
              ),
            }
          )
        );
      }
    } catch (e) {}
  };

  const handleDeployHistoryItem = ({
    applicationId,
    id: deployId,
  }: {
    id: string;
    applicationId?: string;
  }) => {
    if (applicationId) {
      history.push({
        pathname: `/project/${projectId}/deployments/new`,
        search:
          '?' +
          new URLSearchParams({
            deployId,
            origin: 'test',
            applicationId: applicationId,
          }).toString(),
      });
    } else {
      history.push(`/project/${projectId}/deployments/new`);
    }
  };

  const calculateApplicationNames = (): Record<string, string> => {
    return applications.reduce(
      (options, item) => ({
        ...options,
        [item.id]: item.name,
      }),
      {}
    );
  };

  const refreshProjectAndResetView = () => {
    fetchProjectData(projectId);
    dispatch(resetDeploy());
  };

  if (projectData.defaultVideoInputId && defaultInput === '') {
    streamingDevices.forEach(device => {
      if (projectData.defaultVideoInputId === device.id && defaultInput === '') {
        setDefaultInput(device.id);
        dispatch(
          setDeployConfig({
            deploymentId: latestApplicationId,
            testSource: 'USER_DATA',
            inferenceSource: 'FILE',
            videoFile: device.id,
          })
        );
      }
    });
  }

  const renderCancelButton = () => (
    <Button
      variant="contained"
      fullWidth
      size="large"
      onClick={event => {
        event.preventDefault();
        handleCancel(undefined, streamSubject);
        dispatch(setStreamReady(false));
      }}
      color="secondary"
    >
      <FormattedMessage id="form.cancel" />
    </Button>
  );

  const handleSubmitFunc = useCallback(
    (
      deployConfig: DeployConfigState,
      streamSubject: VideoStreamSubject,
      afterTest: Function
    ) => {
      setTaskInProgress(true);
      handleSubmit(deployConfig, streamSubject, afterTest);
    },
    [handleSubmit]
  );

  return (
    <div
      className={cn('dplmt-config-panel', shouldBeVisible ? '' : 'hide', className)}
      data-testid="deployment-config-panel"
      id="deployment-config-panel"
    >
      <DeployExportDialog
        isOpen={isExportModalOpen}
        onClose={() => dispatch(setIsExportModalOpen(false))}
        projectId={projectId}
        deploymentId={deployConfig.deploymentId}
        applicationNames={calculateApplicationNames()}
      />
      <DuplicateDialog
        isOpen={isDuplicateModalOpen}
        onRename={() => {
          dispatch(renameDuplicates());
          dispatch(setIsDuplicateModalOpen(false));
        }}
        onClose={() => {
          dispatch(setPendingFiles([]));
          dispatch(setIsDuplicateModalOpen(false));
        }}
      />
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'terminate.testTitle'})}
        message={intl.formatMessage({id: 'terminate.testContent'})}
        open={confirmEndTask}
        onClose={() => {
          setConfirmEndTask(false);
        }}
        onOk={() => {
          handleCancel();
          setConfirmEndTask(false);
        }}
        submitLabel={intl.formatMessage({id: 'action.terminate'})}
      />
      <DeleteApplicationDialog
        isOpen={isDeleteModalOpen}
        onClose={() => dispatch(setIsDeleteModalOpen(false))}
        projectId={projectId}
        applicationId={deployConfig.deploymentId}
        applicationNames={calculateApplicationNames()}
        onSuccess={refreshProjectAndResetView}
      />
      <AddStreamingDeviceForm
        onAddedAsset={(name: string, url: string): void => {
          setAddedAsset({name, url});
        }}
      />
      <div className="dplmt-tabs-container">
        <StudioTabs
          variant="fullWidth"
          className="dplmt-tabs"
          value={selectedTab}
          onChange={(_event, index) => {
            if (index === 0) {
              if (isTaskInProgress) {
                setConfirmEndTask(true);
              } else {
                dispatch(resetDeploy());
                dispatch(
                  setDeployConfig({
                    deploymentId: latestApplicationId,
                    nodeId: gspList?.[0]?.id ?? '',
                    testSource: '',
                  })
                );
              }
            } else {
              selectLatestHistoryItem();
            }
            onTabChange(index);
          }}
        >
          <StudioTab
            label={intl.formatMessage({id: 'test.tabName'})}
            data-testid="new-test-tab"
          />
          <StudioTab
            label={intl.formatMessage({id: 'testHistory.tabName'})}
            data-testid="test-history-tab"
            disabled={isTaskInProgress}
          />
        </StudioTabs>
      </div>
      <StudioTabPanel className="dplmt-tab-panel" value={selectedTab} index={0}>
        <DeployConfigPanelForm
          handleSubmit={handleSubmitFunc}
          streamSubject={streamSubject}
          fetchImageSets={fetchImageSets}
          isTaskInProgress={isTaskInProgress}
          applications={applications}
          datasetExists={datasetExists}
          projectId={projectId}
          readyToRun={readyToRun}
          renderCancelButton={renderCancelButton}
          calculateApplicationNames={calculateApplicationNames}
          availableTestSources={availableTestSources}
          defaultUserDataSource={defaultUserDataSource}
          datasetSourceSelected={datasetSourceSelected}
          setIsPreProcessingValid={setIsPreProcessingValid}
          setIsPostProcessingValid={setIsPostProcessingValid}
          setConfirmDeleteDevice={setConfirmDeleteDevice}
          isDeploying={isDeploying}
          videoSupported={videoSupported}
          imagesSupported={imagesSupported}
        />
      </StudioTabPanel>
      <StudioTabPanel className="dplmt-tab-panel" value={selectedTab} index={1}>
        <div className="dplmt-scrollable-container">
          <StudioSearch
            placeholder={intl.formatMessage({id: 'test.searchPlaceholder'})}
            onChange={event => debouncedOnSearch(event.target.value)}
          />
          <div className="dplmt-test-histories">
            {historyItems?.entries.map(historyItem => (
              <HistoryItem
                key={historyItem.id}
                deployment={historyItem}
                application={applications.find(
                  application => application.id === historyItem.applicationId
                )}
                onDelete={({id}) => {
                  mutateHistoryItems(
                    {
                      count: historyItems?.count ?? 0,
                      entries:
                        historyItems?.entries?.filter(item => item.id !== id) ?? [],
                    },
                    false
                  );
                  dispatch(
                    setSelectedDeploymentIds(
                      selectedDeploymentIds.filter(deploymentId => deploymentId !== id)
                    )
                  );
                }}
                onSelect={({ids}) => {
                  if (isTaskInProgress) {
                    setConfirmEndTask(true);
                  } else {
                    dispatch(setSelectedDeploymentIds(ids));
                    dispatch(
                      setDeployConfig({
                        environment: historyItem.environment,
                      })
                    );
                  }
                }}
                onDeploy={handleDeployHistoryItem}
                search={search}
                selectedIds={selectedDeploymentIds}
              />
            ))}
          </div>
        </div>
        <div className="dplmt-config-action-buttons">
          <Button
            variant="contained"
            fullWidth
            size="large"
            color="primary"
            type="submit"
            disabled={selectedDeploymentIds.length < 2}
            onClick={() => {
              history.push(
                `/project/${projectId}/test/compare?deploymentIds=${selectedDeploymentIds.join(
                  ','
                )}`
              );
            }}
          >
            <FormattedMessage id="testCompare.compareTests" />
          </Button>
          {deployTask.status === TASK_STATUS.IN_PROGRESS && renderCancelButton()}
        </div>
      </StudioTabPanel>
      {inferenceTask.status === TASK_STATUS.IN_PROGRESS && (
        <FileUploadProgress
          total={inferenceTask.totalSize}
          progress={inferenceTask.uploadProgress}
        />
      )}
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage(
          {id: 'test.deleteDeviceConfirmTitle'},
          {
            type: _.startCase(
              deployConfig.inferenceSource === 'FILE'
                ? intl.formatMessage({id: 'test.form.videoFile'})
                : intl.formatMessage({id: 'test.form.ipCamera'})
            ),
          }
        )}
        message={intl.formatMessage(
          {id: 'test.deleteDeviceConfirmMessage'},
          {
            type: _.lowerCase(
              deployConfig.inferenceSource === 'FILE'
                ? intl.formatMessage({id: 'test.form.videoFile'})
                : intl.formatMessage({id: 'test.form.ipCamera'})
            ),
          }
        )}
        submitLabel={intl.formatMessage({
          id: 'form.delete',
        })}
        open={confirmDeleteDevice.isOpen}
        onClose={() => setConfirmDeleteDevice({isOpen: false, id: null})}
        onOk={() => {
          confirmDeleteDevice.id && handleDeviceDelete(confirmDeleteDevice.id);
          setConfirmDeleteDevice({isOpen: false, id: null});
        }}
      />
    </div>
  );
};
